import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@cargomatic/ui';
import { cn } from 'utils/ui/classes';

const CargomaticPane = ({
  className,
  show,
  width,
  renderBody,
  onHide,
  location,
  title,
}) => {
  const contentRef = useRef(null);
  const [mounted, setMounted] = useState(show);

  useEffect(() => {
    if (show) setMounted(true);
  }, [show]);

  const handleOpenChange = useCallback(
    (open) => {
      if (!open) onHide();
    },
    [onHide],
  );

  const handleAnimationEnd = useCallback(() => {
    if (!show) setMounted(false);
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const handlePointerDown = (e) => {
      if (contentRef.current && contentRef.current.contains(e.target)) return;
      if (
        e.target.closest(
          '[data-radix-popper-content-wrapper], [role="dialog"], [role="alertdialog"]',
        )
      )
        return;
      onHide();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [show, onHide]);

  if (!mounted) return null;

  const side = location === 'left' ? 'left' : 'right';

  const style = width ? { width: window.innerWidth * width } : undefined;

  return (
    <Sheet open={show} onOpenChange={handleOpenChange}>
      <SheetContent
        side={side}
        className={cn(
          'sm:w-1/3 sm:max-w-none flex flex-col gap-0 p-0 [&>button:last-child]:hidden',
          className,
        )}
        style={style}
        onAnimationEnd={handleAnimationEnd}
      >
        <div ref={contentRef} className="flex h-full flex-col">
          <SheetHeader className="sr-only">
            <SheetTitle>{title || 'Panel'}</SheetTitle>
            <SheetDescription />
          </SheetHeader>
          <button
            onClick={onHide}
            className="absolute right-2 top-2 z-20 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex-1 overflow-y-auto">{renderBody()}</div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

CargomaticPane.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  renderBody: PropTypes.func,
  location: PropTypes.string,
  className: PropTypes.string,
  width: PropTypes.number,
  title: PropTypes.string,
};

CargomaticPane.defaultProps = {
  show: true,
  location: 'right',
};

export default CargomaticPane;
